import { useState, type FormEvent } from 'react';
import { api, saveSession, type Session } from './api.js';

/**
 * Sign in. Not sign up.
 *
 * Accounts are created in the Android app and nowhere else. There is no mode
 * switch on this screen and no path to one; the server refuses a web signup
 * outright, so this is the honest face of an actual rule rather than a hidden
 * button. Somebody arriving here without an account is pointed at the app.
 */
export function Login({ onIn }: { onIn: (s: Session) => void }): JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const s = await api<Session>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      saveSession(s);
      onIn(s);
    } catch (e2) {
      setErr((e2 as Error).message);
    } finally { setBusy(false); }
  }

  return (
    <main className="shell narrow">
      <div className="brand">
        <h1>Orbit Health</h1>
        <p className="sub">Sign in</p>
      </div>

      <form className="card" onSubmit={submit}>
        <label htmlFor="email">Email</label>
        <input id="email" type="email" autoComplete="username" required inputMode="email"
               value={email} onChange={(e) => setEmail(e.target.value)} />

        <label htmlFor="password">Password</label>
        <input id="password" type="password" autoComplete="current-password" required
               value={password} onChange={(e) => setPassword(e.target.value)} />

        {err && <p className="bad">{err}</p>}

        <button type="submit" disabled={busy || !email || !password} className="primary wide">
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>

      {/*
        Nothing here advertises signing up or where to get the app.

        This page is on the public internet. A stranger who reads "install it
        and sign up" and does exactly that costs real money -- every account
        runs AI analysis against a paid API -- so the way in is not published.
        People who should have it are given the link directly.

        The signup endpoint refuses browsers outright, so this is not the
        control that keeps the door shut; it is the sign that stops people
        trying the handle.
      */}
    </main>
  );
}
